import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Save, Plus, Trash2, ArrowLeft, User, GitBranch, Circle, Diamond, ArrowRight, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANCH_ACTIONS = {
  next_step: 'Continue to next step',
  end_workflow: 'End workflow',
  goto_step: 'Go to specific step'
};

export default function FMSFlowchartEditor() {
  const { id } = useParams();
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState([]);
  const [users, setUsers] = useState([]);
  const [allFlowcharts, setAllFlowcharts] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Event-driven FMS configuration
  const [startTrigger, setStartTrigger] = useState('');
  const [linkedEntityType, setLinkedEntityType] = useState('');
  const [isAutoTriggered, setIsAutoTriggered] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  
  // Dialog state for adding new step
  const [showStepTypeDialog, setShowStepTypeDialog] = useState(false);
  const [newStepType, setNewStepType] = useState('regular');

  useEffect(() => {
    if (id && id !== 'new') {
      fetchFlowchart();
    }
    fetchEmployees();
    fetchAllFlowcharts();
    fetchEventTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/fms/event-types`, { headers: getAuthHeader() });
      setEventTypes(response.data.event_types || []);
      setEntityTypes(response.data.entity_types || []);
    } catch (error) {
      console.error('Failed to fetch event types');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees`, { headers: getAuthHeader() });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };

  const fetchAllFlowcharts = async () => {
    try {
      const response = await axios.get(`${API_URL}/fms/flowcharts`, { headers: getAuthHeader() });
      setAllFlowcharts(response.data);
    } catch (error) {
      console.error('Failed to fetch flowcharts');
    }
  };

  const fetchFlowchart = async () => {
    try {
      const response = await axios.get(`${API_URL}/fms/flowcharts/${id}`, { headers: getAuthHeader() });
      const flowchart = response.data;
      setName(flowchart.name);
      setDescription(flowchart.description);
      // Load trigger settings
      setStartTrigger(flowchart.start_trigger || '');
      setLinkedEntityType(flowchart.linked_entity_type || '');
      setIsAutoTriggered(flowchart.is_auto_triggered || false);
      // Convert total hours to days+hours for editing
      const stepsWithDaysHours = (flowchart.steps || []).map(s => ({
        ...s,
        tat_days: Math.floor((s.tat_hours || 0) / 24),
        tat_hours: (s.tat_hours || 0) % 24,
        step_type: s.step_type || 'regular',
        completion_trigger: s.completion_trigger || '',
        yes_branch: s.yes_branch || { action: 'next_step' },
        no_branch: s.no_branch || { action: 'next_step' }
      }));
      setSteps(stepsWithDaysHours);
    } catch (error) {
      toast.error('Failed to fetch flowchart');
    }
  };

  const handleAddStepClick = () => {
    setNewStepType('regular');
    setShowStepTypeDialog(true);
  };

  const handleConfirmAddStep = () => {
    const newStep = {
      id: `step-${Date.now()}`,
      title: '',
      description: '',
      tat_days: 0,
      tat_hours: 0,
      doer_id: '',
      step_type: newStepType,
      completion_trigger: '',  // Event that auto-completes this step
      yes_branch: { action: 'next_step', target_fms_id: '', target_step_id: '' },
      no_branch: { action: 'next_step', target_fms_id: '', target_step_id: '' }
    };
    setSteps([...steps, newStep]);
    setShowStepTypeDialog(false);
  };

  const handleUpdateStep = (index, field, value) => {
    const updatedSteps = [...steps];
    updatedSteps[index] = { ...updatedSteps[index], [field]: value };
    setSteps(updatedSteps);
  };

  const handleUpdateBranch = (index, branchType, field, value) => {
    setSteps(prevSteps => {
      const updatedSteps = [...prevSteps];
      updatedSteps[index] = {
        ...updatedSteps[index],
        [branchType]: {
          ...updatedSteps[index][branchType],
          [field]: value
        }
      };
      return updatedSteps;
    });
  };
  
  const handleUpdateBranchFMS = (index, branchType, fmsId) => {
    setSteps(prevSteps => {
      const updatedSteps = [...prevSteps];
      updatedSteps[index] = {
        ...updatedSteps[index],
        [branchType]: {
          ...updatedSteps[index][branchType],
          target_fms_id: fmsId,
          target_step_id: ''  // Reset step when FMS changes
        }
      };
      return updatedSteps;
    });
  };

  const handleRemoveStep = (index) => {
    setSteps(steps.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!name || !description || steps.length === 0) {
      toast.error('Please fill all required fields and add at least one step');
      return;
    }

    if (steps.some(s => !s.title || !s.doer_id || (s.tat_days === 0 && s.tat_hours === 0))) {
      toast.error('Please fill Title, TAT (at least 1 hour or 1 day), and Doer for all steps');
      return;
    }

    // Validate decision step branches
    for (const step of steps) {
      if (step.step_type === 'decision') {
        if (step.yes_branch.action === 'goto_step' && (!step.yes_branch.target_fms_id || !step.yes_branch.target_step_id)) {
          toast.error(`Please select target FMS and Step for YES branch in "${step.title}"`);
          return;
        }
        if (step.no_branch.action === 'goto_step' && (!step.no_branch.target_fms_id || !step.no_branch.target_step_id)) {
          toast.error(`Please select target FMS and Step for NO branch in "${step.title}"`);
          return;
        }
      }
    }

    // Convert days+hours to total hours for storage
    const stepsWithTotalHours = steps.map(s => ({
      ...s,
      tat_hours: (s.tat_days || 0) * 24 + (s.tat_hours || 0),
      completion_trigger: s.completion_trigger || null
    }));

    setLoading(true);
    try {
      const data = { 
        name, 
        description, 
        steps: stepsWithTotalHours,
        start_trigger: startTrigger || null,
        linked_entity_type: linkedEntityType || null,
        is_auto_triggered: isAutoTriggered
      };
      
      if (id === 'new') {
        await axios.post(`${API_URL}/fms/flowcharts`, data, { headers: getAuthHeader() });
        toast.success('Flowchart created successfully');
      } else {
        await axios.patch(`${API_URL}/fms/flowcharts/${id}`, data, { headers: getAuthHeader() });
        toast.success('Flowchart updated successfully');
      }
      navigate('/fms');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save flowchart');
    } finally {
      setLoading(false);
    }
  };

  // Get steps for a specific flowchart (for branch selection)
  const getFlowchartSteps = (fmsId) => {
    if (fmsId === id || fmsId === 'current') {
      return steps;
    }
    const flowchart = allFlowcharts.find(f => f.id === fmsId);
    return flowchart?.steps || [];
  };

  // Render branch configuration UI
  const renderBranchConfig = (step, index, branchType, label, icon) => {
    const branch = step[branchType];
    const Icon = icon;
    
    return (
      <div className="p-3 bg-muted/30 rounded-lg border">
        <div className="flex items-center gap-2 mb-3">
          <Icon className={`w-4 h-4 ${branchType === 'yes_branch' ? 'text-green-600' : 'text-red-600'}`} />
          <Label className="font-semibold">{label}</Label>
        </div>
        
        <div className="space-y-3">
          <Select
            value={branch.action}
            onValueChange={(val) => handleUpdateBranch(index, branchType, 'action', val)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select action..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="next_step">Continue to next step</SelectItem>
              <SelectItem value="end_workflow">End workflow</SelectItem>
              <SelectItem value="goto_step">Go to specific step</SelectItem>
            </SelectContent>
          </Select>
          
          {branch.action === 'goto_step' && (
            <div className="grid grid-cols-2 gap-2">
              <Select
                value={branch.target_fms_id || 'select_fms'}
                onValueChange={(val) => {
                  const fmsId = val === 'select_fms' ? '' : val;
                  handleUpdateBranchFMS(index, branchType, fmsId);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="-- Select FMS --" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="select_fms">-- Select FMS --</SelectItem>
                  <SelectItem value="current">Current FMS</SelectItem>
                  {allFlowcharts.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select
                value={branch.target_step_id || 'select_step'}
                onValueChange={(val) => handleUpdateBranch(index, branchType, 'target_step_id', val === 'select_step' ? '' : val)}
                disabled={!branch.target_fms_id || branch.target_fms_id === 'select_fms'}
              >
                <SelectTrigger>
                  <SelectValue placeholder="-- Select Step --" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="select_step">-- Select Step --</SelectItem>
                  {getFlowchartSteps(branch.target_fms_id === 'current' ? id : branch.target_fms_id).map((s, i) => (
                    <SelectItem key={s.id} value={s.id}>
                      Step {i + 1}: {s.title || '(Untitled)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Check if a decision step terminates the current FMS flow
  // (both branches either go to another FMS or end the workflow)
  const isTerminatingDecision = (step) => {
    if (step.step_type !== 'decision') return false;
    
    const yesBranch = step.yes_branch || {};
    const noBranch = step.no_branch || {};
    
    // Check if YES branch leaves current FMS
    const yesLeavesFlow = yesBranch.action === 'end_workflow' || 
      (yesBranch.action === 'goto_step' && yesBranch.target_fms_id && yesBranch.target_fms_id !== 'current');
    
    // Check if NO branch leaves current FMS
    const noLeavesFlow = noBranch.action === 'end_workflow' || 
      (noBranch.action === 'goto_step' && noBranch.target_fms_id && noBranch.target_fms_id !== 'current');
    
    return yesLeavesFlow && noLeavesFlow;
  };
  
  // Check if there are any terminating decision steps before this index
  const hasTerminatingDecisionBefore = (beforeIndex) => {
    for (let i = 0; i < beforeIndex; i++) {
      if (isTerminatingDecision(steps[i])) {
        return true;
      }
    }
    return false;
  };
  
  // Check if we can add more steps (no terminating decision exists)
  const canAddMoreSteps = () => {
    return !steps.some(step => isTerminatingDecision(step));
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">You don't have permission to access this page</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/fms')} data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="text-3xl font-bold tracking-tight">
                {id === 'new' ? 'Create' : 'Edit'} Flowchart
              </h2>
            </div>
          </div>
          <Button onClick={handleSave} disabled={loading} data-testid="save-flowchart-btn">
            <Save className="w-4 h-4 mr-2" />
            Save Flowchart
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Flowchart Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                data-testid="flowchart-name"
                value={name}
                onChange={(e) => setName(e.target.value.toUpperCase())}
                placeholder="ENTER FLOWCHART NAME"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                data-testid="flowchart-description"
                value={description}
                onChange={(e) => setDescription(e.target.value.toUpperCase())}
                placeholder="ENTER FLOWCHART DESCRIPTION"
                rows={3}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            
            {/* Event-Driven FMS Configuration */}
            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-sm">Auto-Trigger</span>
                Workflow Automation
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Configure this workflow to automatically start when a specific ERP event occurs.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startTrigger">Start Trigger Event</Label>
                  <Select
                    value={startTrigger || 'none'}
                    onValueChange={(val) => {
                      setStartTrigger(val === 'none' ? '' : val);
                      if (val && val !== 'none') setIsAutoTriggered(true);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select trigger event..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (Manual Start)</SelectItem>
                      {eventTypes.map(et => (
                        <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Event that will auto-create this workflow
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="linkedEntityType">Linked Entity Type</Label>
                  <Select
                    value={linkedEntityType || 'none'}
                    onValueChange={(val) => setLinkedEntityType(val === 'none' ? '' : val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select entity type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {entityTypes.map(et => (
                        <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Entity this workflow will track
                  </p>
                </div>
                
                <div className="space-y-2 flex items-end">
                  <div className="flex items-center gap-2 p-3 border rounded-lg w-full">
                    <input
                      type="checkbox"
                      id="isAutoTriggered"
                      checked={isAutoTriggered}
                      onChange={(e) => setIsAutoTriggered(e.target.checked)}
                      className="h-4 w-4"
                    />
                    <Label htmlFor="isAutoTriggered" className="cursor-pointer">
                      Enable Auto-Trigger
                    </Label>
                  </div>
                </div>
              </div>
              
              {startTrigger && isAutoTriggered && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                  <strong>✓ Auto-Trigger Enabled:</strong> This workflow will automatically start when "{eventTypes.find(e => e.value === startTrigger)?.label || startTrigger}" event occurs.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Workflow Steps</CardTitle>
              <div className="flex items-center gap-2">
                {!canAddMoreSteps() && (
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                    Flow ends at decision step
                  </span>
                )}
                <Button 
                  onClick={handleAddStepClick} 
                  size="sm" 
                  data-testid="add-step-btn"
                  disabled={!canAddMoreSteps()}
                  title={!canAddMoreSteps() ? "Cannot add steps after a decision that exits the workflow" : ""}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Step
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {steps.map((step, index) => {
                const isUnreachable = hasTerminatingDecisionBefore(index);
                const isTerminating = isTerminatingDecision(step);
                
                return (
                <div 
                  key={step.id} 
                  className={`p-4 border rounded-lg ${
                    isUnreachable 
                      ? 'border-red-300 bg-red-50/50 opacity-60' 
                      : step.step_type === 'decision' 
                        ? isTerminating
                          ? 'border-orange-400 bg-orange-50/50'
                          : 'border-amber-300 bg-amber-50/50' 
                        : 'border-border'
                  }`}
                  data-testid={`step-${index}`}
                >
                  {isUnreachable && (
                    <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded text-red-700 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      <span>⚠️ This step is unreachable - a previous decision exits the workflow</span>
                    </div>
                  )}
                  {isTerminating && (
                    <div className="mb-3 p-2 bg-orange-100 border border-orange-300 rounded text-orange-700 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      <span>🛑 This decision ends the current workflow (both branches exit)</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      {step.step_type === 'decision' ? (
                        <Diamond className="w-5 h-5 text-amber-600 fill-amber-100" />
                      ) : (
                        <Circle className="w-5 h-5 text-blue-600 fill-blue-100" />
                      )}
                      <h4 className="font-semibold">
                        Step {index + 1}
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                          step.step_type === 'decision' 
                            ? 'bg-amber-200 text-amber-800' 
                            : 'bg-blue-200 text-blue-800'
                        }`}>
                          {step.step_type === 'decision' ? 'Decision' : 'Regular'}
                        </span>
                      </h4>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveStep(index)}
                      data-testid={`remove-step-${index}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>
                        {step.step_type === 'decision' ? 'Decision Question *' : 'Title (What\'s to be done) *'}
                      </Label>
                      <Input
                        data-testid={`step-title-${index}`}
                        value={step.title}
                        onChange={(e) => handleUpdateStep(index, 'title', e.target.value.toUpperCase())}
                        placeholder={step.step_type === 'decision' ? 'IS QUALITY CHECK PASSED?' : 'STEP TITLE'}
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>TAT (When) *</Label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-1">
                            <Input
                              data-testid={`step-tat-days-${index}`}
                              type="number"
                              value={step.tat_days || 0}
                              onChange={(e) => handleUpdateStep(index, 'tat_days', parseInt(e.target.value) || 0)}
                              min="0"
                              className="w-16"
                            />
                            <span className="text-sm text-muted-foreground">Days</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-1">
                            <Input
                              data-testid={`step-tat-hours-${index}`}
                              type="number"
                              value={step.tat_hours || 0}
                              onChange={(e) => handleUpdateStep(index, 'tat_hours', parseInt(e.target.value) || 0)}
                              min="0"
                              max="23"
                              className="w-16"
                            />
                            <span className="text-sm text-muted-foreground">Hours</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Doer (Who should do it) *</Label>
                      <Select 
                        value={step.doer_id || 'unassigned'} 
                        onValueChange={(val) => handleUpdateStep(index, 'doer_id', val === 'unassigned' ? '' : val)}
                      >
                        <SelectTrigger data-testid={`step-doer-${index}`}>
                          <SelectValue placeholder="Select doer..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">-- Select Doer --</SelectItem>
                          {users.map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name} ({u.designation_name || 'User'})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 md:col-span-4">
                      <Label>Description (How it's to be done)</Label>
                      <Textarea
                        data-testid={`step-description-${index}`}
                        value={step.description}
                        onChange={(e) => handleUpdateStep(index, 'description', e.target.value.toUpperCase())}
                        placeholder="DETAILED INSTRUCTIONS FOR THIS STEP..."
                        rows={2}
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>
                    
                    {/* Completion Trigger - available for ALL steps */}
                    <div className="space-y-2 md:col-span-4">
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-medium">Auto-Complete</span>
                          <Label className="font-medium text-green-800">Completion Trigger (Optional)</Label>
                        </div>
                        <Select
                          value={step.completion_trigger || 'none'}
                          onValueChange={(val) => handleUpdateStep(index, 'completion_trigger', val === 'none' ? '' : val)}
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue placeholder="Select event to auto-complete this step..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None (Manual Completion)</SelectItem>
                            {eventTypes.map(et => (
                              <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-green-700 mt-1">
                          When this event occurs, this step will be automatically marked as complete.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Branch Configuration for Decision Steps */}
                  {step.step_type === 'decision' && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center gap-2 mb-3">
                        <GitBranch className="w-4 h-4 text-amber-600" />
                        <Label className="font-semibold text-amber-800">Branch Configuration</Label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {renderBranchConfig(step, index, 'yes_branch', 'If YES', CheckCircle)}
                        {renderBranchConfig(step, index, 'no_branch', 'If NO', XCircle)}
                      </div>
                    </div>
                  )}
                </div>
              )})}

              {steps.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <p>No steps added yet. Click "Add Step" to create workflow steps.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Step Type Selection Dialog */}
      <Dialog open={showStepTypeDialog} onOpenChange={setShowStepTypeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Choose Step Type</DialogTitle>
            <DialogDescription>
              Select the type of step you want to add to this workflow.
            </DialogDescription>
          </DialogHeader>
          
          <RadioGroup value={newStepType} onValueChange={setNewStepType} className="space-y-4 py-4">
            <div 
              className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${
                newStepType === 'regular' ? 'border-blue-500 bg-blue-50' : 'hover:bg-muted/50'
              }`}
              onClick={() => setNewStepType('regular')}
            >
              <RadioGroupItem value="regular" id="regular" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Circle className="w-5 h-5 text-blue-600 fill-blue-100" />
                  <Label htmlFor="regular" className="font-semibold cursor-pointer">Regular Step</Label>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  A standard workflow step. Completing this step will automatically trigger the next step in sequence.
                </p>
              </div>
            </div>
            
            <div 
              className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${
                newStepType === 'decision' ? 'border-amber-500 bg-amber-50' : 'hover:bg-muted/50'
              }`}
              onClick={() => setNewStepType('decision')}
            >
              <RadioGroupItem value="decision" id="decision" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Diamond className="w-5 h-5 text-amber-600 fill-amber-100" />
                  <Label htmlFor="decision" className="font-semibold cursor-pointer">Decision Step</Label>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  A branching step with YES/NO paths. Each path can continue to the next step, end the workflow, or jump to any step in any FMS.
                </p>
              </div>
            </div>
          </RadioGroup>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStepTypeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmAddStep}>
              Add {newStepType === 'decision' ? 'Decision' : 'Regular'} Step
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
